'use client'

import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Box } from '@mui/material'
import LoginIcon from '@mui/icons-material/Login'
import LinkIcon from '@mui/icons-material/Link'
import { useCatch, useEffectAsync } from '../reactHelper'
import { formatBoolean, formatTime } from '../common/util/formatter'
import { useTranslation } from '../common/components/LocalizationProvider'
import PageLayout from '../common/components/PageLayout'
import SettingsMenu from './components/SettingsMenu'
import CollectionFab from './components/CollectionFab'
import CollectionActions from './components/CollectionActions';
import { useManager } from '../common/util/permissions'
import useSettingsStyles from './common/useSettingsStyles'
import DataTable from '../common/components/Datatable'
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import LogoImage from '../login/LogoImage'
import { nativePostMessage } from '../common/components/NativeInterface'
import { sessionActions } from '../store'
import { useTheme } from '@mui/material/styles';

// Estilos para impressão que garantem legibilidade independente do tema
const printStyles = {
  container: {
    backgroundColor: '#ffffff',
    color: '#000000',
  },
  header: {
    padding: 10,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 24,
    margin: 0,
    color: '#000000',
  },
  subtitle: {
    marginTop: 10,
    fontSize: 14,
    color: '#555555',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableHeadCell: {
    padding: '8px 16px',
    fontWeight: 'bold',
    color: '#000000',
    borderBottom: '2px solid #dddddd',
  },
  tableCell: {
    padding: '8px 16px',
    color: '#000000',
    borderBottom: '1px solid #dddddd',
  },
  strong: {
    fontWeight: 'bold',
    color: '#000000',
  },
};

// Componente para cabeçalho de impressão
const PrintHeader = ({ title }) => {
  const t = useTranslation()

  return (
    <div style={printStyles.header}>
      <div style={printStyles.headerContent}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print()
                nativePostMessage(`print|${title}.pdf`)
              }),
            )
          }}
          alt={t('companyLogo')}
        />
        <h1 style={printStyles.title}>{title}</h1>
      </div>
      <div style={printStyles.subtitle}>
        <div>
          <strong style={printStyles.strong}>{t('costDate')}:</strong> {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  )
};

const UsersPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();
  const t = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const printing = useSelector((state) => state.session.printing)
  const manager = useManager()

  const [timestamp, setTimestamp] = useState(Date.now())
  const [items, setItems] = useState([])
  const [searchKeyword, setSearchKeyword] = useState('')
  const [loading, setLoading] = useState(false)
  const [temporary, setTemporary] = useState(false)

  // Adicionar evento de afterprint para limpar o estado de impressão
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false))
    }
    window.addEventListener('afterprint', handleAfterPrint)
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint)
    }
  }, [dispatch])

  const handleLogin = useCatch(async (userId) => {
    const response = await fetch(`/api/session/${userId}`);
    if (response.ok) {
      window.location.replace('/');
    } else {
      throw Error(await response.text());
    }
  });

  const actionLogin = {
    key: 'login',
    title: t('loginLogin'),
    icon: <LoginIcon fontSize="small" />,
    handler: handleLogin,
  };

  const actionConnections = {
    key: 'connections',
    title: t('sharedConnections'),
    icon: <LinkIcon fontSize="small" />,
    handler: (userId) => navigate(`/settings/user/${userId}/connections`),
  };

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/users');
      if (response.ok) {
        setItems(await response.json());
      } else {
        throw Error(await response.text());
      }
    } finally {
      setLoading(false);
    }
  }, [timestamp])

  const handleExport = () => {
    window.location.assign('/api/reports/users/xlsx')
  };

  const handlePdfExport = () => {
    // Ativar modo de impressão
    dispatch(sessionActions.setPrinting(true))
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 80, disableColumnMenu: true },
    { field: 'name', headerName: t('sharedName'), flex: 1,  disableColumnMenu: true },
    { field: 'email', headerName: t('userEmail'), flex: 1,  disableColumnMenu: true },
    { field: 'userAdmin', headerName: t('userAdmin'), flex: 1,  disableColumnMenu: true, renderCell: (params) => (
      formatBoolean(params.row.administrator, t)
    ), },
    { field: 'sharedDisabled', headerName: t('sharedDisabled'), flex: 1,  disableColumnMenu: true, renderCell: (params) => (
      formatBoolean(params.row.disabled, t)
    ), },
    { field: 'userExpirationTime', headerName: t('userExpirationTime'), flex: 1,  disableColumnMenu: true, renderCell: (params) => (
      formatTime(params.row.expirationTime, 'date')
    ), },
    {
      field: 'actions',
      headerName: t('actions'),
      sortable: false,
      renderCell: (params) => (
        <div>
          <CollectionActions
            itemId={params.row.id}
            editPath='/settings/user'
            endpoint='users'
            setTimestamp={setTimestamp}
            customActions={manager ? [actionLogin, actionConnections] : [actionConnections]}
          />
        </div>
      ),
      width: 150,  // Set a fixed width for the actions column

      disableColumnMenu: true,
    }
  ];

  // Layout para modo de impressão
  if (printing) {
    return (
      <div style={printStyles.container}>
        <PrintHeader title={t('settingsUsers')} />
        <div>
          <table style={printStyles.table}>
            <thead style={printStyles.tableHead}>
              <tr>
                {columns.map((column) => (
                  <th key={column.field} style={printStyles.tableHeadCell}>
                    {column.headerName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  {columns.map((column) => (
                    <td key={column.field} style={printStyles.tableCell}>
                      {column.field === 'administrator' || column.field === 'disabled'
                        ? formatBoolean(item[column.field], t)
                        : column.field === 'expirationTime'
                          ? formatTime(item.expirationTime, 'date')
                          : item[column.field]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'settingsUsers']}>
      <DataTable
        tableName='users'
        columns={columns}
        rows={items}
        loading={loading}
        filterable={true} // Habilita o campo de pesquisa
        enablePdfExport={manager} // Mostra botão PDF apenas para managers
        enableXlsxExport={manager} // Mostra botão XLSX apenas para managers
        onPdfExport={handlePdfExport} // Adicione esta prop
        onXlsxExport={handleExport} // Adicione esta prop
      />
      <CollectionFab editPath='/settings/user' />
    </PageLayout>
  );
};

export default UsersPage;
